import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';
import { useLocale } from '../contexts/LocaleContext';

const CONTACT = 'contact@adfini.ai';

export default function RefundPolicy() {
  const { locale } = useLocale();
  const lastUpdated = 'April 30, 2026';

  return (
    <LegalPageLayout title="Refund Policy" locale={locale}>
      <p className="text-[var(--studio-ink)]">Last updated: {lastUpdated}</p>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Overview</h2>
        <p>
          Adfini sells digital credit packs used to run creative workflows in the application.
          Credits are delivered to your account after successful payment. This policy explains how we
          handle refund requests.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">General rule</h2>
        <p>
          Because credits are digital goods that can be consumed immediately, <strong className="text-[var(--studio-ink)]">all sales are
          generally final</strong> once credits have been added to your account and/or used. We do not
          guarantee refunds for change of mind or unused credits except where required by law or as
          described below.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">When we may issue a refund or adjustment</h2>
        <p>We may offer a refund, credit adjustment, or other remedy when:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            A payment was duplicated or charged in error through our checkout provider.
          </li>
          <li>
            Credits were not delivered to your account after a completed successful payment and we
            can verify the failure on our side.
          </li>
          <li>
            A serious, verified service defect prevented you from using purchased credits in a
            material way, and we cannot reasonably restore the value (assessed case by case).
          </li>
          <li>
            Applicable law in your jurisdiction requires a right of withdrawal or refund for
            digital content (if and as applicable, including any statutory cooling-off period that
            cannot be waived).
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">How to request a refund</h2>
        <p>
          Email{' '}
          <a className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>{' '}
          from the email address on your Adfini account. Include:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Your account email and approximate date of purchase</li>
          <li>Stripe receipt or transaction reference if you have it</li>
          <li>A short description of the issue</li>
        </ul>
        <p>We aim to respond within a reasonable time and may ask follow-up questions to verify your request.</p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Chargebacks</h2>
        <p>
          If you initiate a chargeback or payment dispute, we may suspend or close accounts tied to
          abuse of the payment system, subject to investigation. Please contact us first so we can
          try to resolve the issue directly.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Contact</h2>
        <p>
          Refund questions:{' '}
          <a className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
          . For general help, see{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/help">
            Help &amp; support
          </Link>
          .
        </p>
      </section>
    </LegalPageLayout>
  );
}
